import { Component, OnInit, OnChanges, SimpleChanges, Input } from '@angular/core';

import { ChartModule } from 'primeng/chart';
import { ButtonModule } from 'primeng/button';

type RangeKey = 'three' | 'five' | 'ten' | 'fifteen';

interface CardRow {
    label: string;
    base: string | number;
    scenario: string | number;
    trend: 'up' | 'down' | 'flat';
}

interface CostCard {
    title: string;
    chartData: any;
    chartOptions: any;
    rows: CardRow[];
    total: {
        base: string | number;
        scenario: string | number;
        trend: 'up' | 'down' | 'flat';
    };
    suggestion: string;
}

@Component({
    selector: 'app-cost-analysis',
    standalone: true,
    imports: [ButtonModule, ChartModule],
    templateUrl: './cost-analysis.html'
})
export class CostAnalysisComponent implements OnInit, OnChanges {
    @Input() refreshKey?: string;
    @Input() baseCm: Record<string, Record<string, number>> | null = null;
    @Input() currentCm: Record<string, Record<string, number>> | null = null;
    @Input() range: RangeKey = 'three';
    @Input() yearKey: string = '2045'; // use latest year

    private debug = false; // set true to see detailed logs

    data = {
        cards: <CostCard[]>[
            {
                title: 'Intervention Cost ($)',
                chartData: this.buildChart(['Base', 'Current', 'Difference'], [0, 0, 0], 'M'),
                chartOptions: this.barOptions('M', 30),
                rows: [
                    { label: 'Capital', base: 0, scenario: 0, trend: 'flat' },
                    { label: 'Running', base: 0, scenario: 0, trend: 'flat' }
                ],
                total: { base: 0, scenario: 0, trend: 'flat' },
                suggestion: '“Implementing scenario xx could save $YY millions in healthcare costs and prevent xxx CVD events over 20 years in your city”'
            },
            {
                title: 'Health System Cost ($)',
                chartData: this.buildChart(['Base', 'Current', 'Difference'], [0, 0, 0], 'M'),
                chartOptions: this.barOptions('M', 30),
                rows: [
                    { label: 'Primary Care', base: 0, scenario: 0, trend: 'flat' },
                    { label: 'Secondary Care', base: 0, scenario: 0, trend: 'flat' }
                ],
                total: { base: 0, scenario: 0, trend: 'flat' },
                suggestion: '“Implementing scenario xx could save $YY millions in healthcare costs and prevent xxx CVD events over 20 years in your city”'
            },
            {
                title: 'Broader Societal Cost ($)',
                chartData: this.buildChart(['Base', 'Current', 'Difference'], [0, 0, 0], 'M'),
                chartOptions: this.barOptions('M', 30),
                rows: [
                    { label: 'Productivity patients and carers', base: 0, scenario: 0, trend: 'flat' },
                    { label: 'Funeral', base: 0, scenario: 0, trend: 'flat' }
                ],
                total: { base: 0, scenario: 0, trend: 'flat' },
                suggestion: '“Implementing scenario xx could save $YY millions in healthcare costs and prevent xxx CVD events over 20 years in your city”'
            }
        ]
    };

    ngOnInit(): void {
        this.populateFromCmCompare(this.baseCm ?? {}, this.currentCm ?? {}, this.range);
        this.applyHatch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['refreshKey'] && !changes['refreshKey'].firstChange) {
            // Force p-chart to re-render by changing options object references
            this.data.cards = this.data.cards.map((card) => ({
                ...card,
                chartOptions: { ...card.chartOptions }
            }));
        }
        this.populateFromCmCompare(this.baseCm ?? {}, this.currentCm ?? {}, this.range);
        this.applyHatch();
    }

    trendIcon(trend: 'up' | 'down' | 'flat') {
        if (trend === 'up') return 'pi pi-arrow-up text-emerald-600';
        if (trend === 'down') return 'pi pi-arrow-down text-red-500';
        return 'pi pi-minus text-slate-400';
    }

    private applyHatch() {
        this.data.cards = this.data.cards.map((card) => ({
            ...card,
            chartData: {
                ...card.chartData,
                datasets: card.chartData.datasets.map((ds: any) => this.hatchThirdBar(ds))
            }
        }));
    }

    private chooseSuggestedMax(unit: 'M' | 'B', valuesScaled: number[]): number {
        const max = Math.max(...valuesScaled, 0);
        if (unit === 'B') {
            // Make 1–2B ranges look better with a 3B axis cap
            if (max <= 2) return 3;
            if (max <= 3) return 4; // small headroom
            if (max <= 5) return 6; // moderate headroom
            return Math.ceil(max * 1.2); // general case
        }
        // Millions: keep a gentle headroom; minimum to avoid cramped bars
        const headroom = Math.max(Math.ceil(max * 1.2), 10);
        return headroom;
    }

    private populateFromCmCompare(base: Record<string, Record<string, number>>, current: Record<string, Record<string, number>>, range: RangeKey): void {
        const baseYear = '2045';
        const currentYear = '2045';

        // Intervention
        const capitalBase = this.valueAt(base, this.keysInterventionCapital(range), baseYear, undefined, false);
        const capitalCurrent = this.valueAt(current, this.keysInterventionCapital(range), currentYear, undefined, true);
        const runningBase = this.valueAt(base, this.keysInterventionRunning(range), baseYear, undefined, false);
        const runningCurrent = this.valueAt(current, this.keysInterventionRunning(range), currentYear, undefined, true);
        const totalInterventionBase = this.valueAt(base, this.keysInterventionTotal(range), baseYear, capitalBase + runningBase, false);
        const totalInterventionCurrent = this.valueAt(current, this.keysInterventionTotal(range), currentYear, capitalCurrent + runningCurrent, true);

        // Health System
        const primaryBase = this.valueAt(base, this.keysPrimaryCare(range), baseYear, undefined, false);
        const primaryCurrent = this.valueAt(current, this.keysPrimaryCare(range), currentYear, undefined, true);
        const secondaryBase = this.valueAt(base, this.keysSecondaryCare(range), baseYear, undefined, false);
        const secondaryCurrent = this.valueAt(current, this.keysSecondaryCare(range), currentYear, undefined, true);
        const totalHealthBase = this.valueAt(base, this.keysHealthSectorTotal(range), baseYear, primaryBase + secondaryBase, false);
        const totalHealthCurrent = this.valueAt(current, this.keysHealthSectorTotal(range), currentYear, primaryCurrent + secondaryCurrent, true);

        // Societal
        const productivityBase = this.valueAt(base, this.keysProductivity(range), baseYear, undefined, false);
        const productivityCurrent = this.valueAt(current, this.keysProductivity(range), currentYear, undefined, true);
        const funeralBase = this.valueAt(base, this.keysFuneral(range), baseYear, undefined, false);
        const funeralCurrent = this.valueAt(current, this.keysFuneral(range), currentYear, undefined, true);
        const socialTotalBase = this.valueAt(base, this.keysSocialTotal(range), baseYear, productivityBase + funeralBase, false);
        const socialTotalCurrent = this.valueAt(current, this.keysSocialTotal(range), currentYear, productivityCurrent + funeralCurrent, true);

        if (this.debug) {
            this.probe('Intervention Capital', this.keysInterventionCapital(range), base, current);
            this.probe('Intervention Running', this.keysInterventionRunning(range), base, current);
            this.probe('Intervention Total', this.keysInterventionTotal(range), base, current);
            this.probe('Primary Care', this.keysPrimaryCare(range), base, current);
            this.probe('Secondary Care', this.keysSecondaryCare(range), base, current);
            this.probe('Health Sector Total', this.keysHealthSectorTotal(range), base, current);
            this.probe('Productivity', this.keysProductivity(range), base, current);
            this.probe('Funeral', this.keysFuneral(range), base, current);
            this.probe('Social Total', this.keysSocialTotal(range), base, current);
            console.log('[CostAnalysis] computed', {
                range,
                baseYear,
                currentYear,
                capitalBase,
                capitalCurrent,
                runningBase,
                runningCurrent,
                totalInterventionBase,
                totalInterventionCurrent,
                primaryBase,
                primaryCurrent,
                secondaryBase,
                secondaryCurrent,
                totalHealthBase,
                totalHealthCurrent,
                productivityBase,
                productivityCurrent,
                funeralBase,
                funeralCurrent,
                socialTotalBase,
                socialTotalCurrent
            });
        }

        // Adaptive unit selection per card
        const interMax = Math.max(capitalBase, capitalCurrent, runningBase, runningCurrent, totalInterventionBase, totalInterventionCurrent, Math.abs(totalInterventionCurrent - totalInterventionBase));
        const interUnit = this.chooseUnit(interMax);
        const interDiv = interUnit === 'B' ? 1_000_000_000 : 1_000_000;
        const interSuggested = this.chooseSuggestedMax(interUnit, [totalInterventionBase / interDiv, totalInterventionCurrent / interDiv, Math.abs(totalInterventionCurrent - totalInterventionBase) / interDiv]);

        const healthMax = Math.max(primaryBase, primaryCurrent, secondaryBase, secondaryCurrent, totalHealthBase, totalHealthCurrent, Math.abs(totalHealthCurrent - totalHealthBase));
        const healthUnit = this.chooseUnit(healthMax);
        const healthDiv = healthUnit === 'B' ? 1_000_000_000 : 1_000_000;
        const healthSuggested = this.chooseSuggestedMax(healthUnit, [totalHealthBase / healthDiv, totalHealthCurrent / healthDiv, Math.abs(totalHealthCurrent - totalHealthBase) / healthDiv]);

        const socialMax = Math.max(productivityBase, productivityCurrent, funeralBase, funeralCurrent, socialTotalBase, socialTotalCurrent, Math.abs(socialTotalCurrent - socialTotalBase));
        const socialUnit = this.chooseUnit(socialMax);
        const socialDiv = socialUnit === 'B' ? 1_000_000_000 : 1_000_000;
        const socialSuggested = this.chooseSuggestedMax(socialUnit, [socialTotalBase / socialDiv, socialTotalCurrent / socialDiv, Math.abs(socialTotalCurrent - socialTotalBase) / socialDiv]);

        const nextCards: CostCard[] = [
            {
                title: 'Intervention Cost ($)',
                chartData: this.buildChart(['Base', 'Current', 'Difference'], [totalInterventionBase / interDiv, totalInterventionCurrent / interDiv, Math.abs(totalInterventionCurrent - totalInterventionBase) / interDiv], interUnit),
                chartOptions: this.barOptions(interUnit, interSuggested),
                rows: [
                    { label: 'Capital', base: this.formatWithUnit(capitalBase, interUnit), scenario: this.formatWithUnit(capitalCurrent, interUnit), trend: this.trendOf(capitalBase, capitalCurrent) },
                    { label: 'Running', base: this.formatWithUnit(runningBase, interUnit), scenario: this.formatWithUnit(runningCurrent, interUnit), trend: this.trendOf(runningBase, runningCurrent) }
                ],
                total: {
                    base: this.formatWithUnit(totalInterventionBase, interUnit),
                    scenario: this.formatWithUnit(totalInterventionCurrent, interUnit),
                    trend: this.trendOf(totalInterventionBase, totalInterventionCurrent)
                },
                suggestion: '“Implementing scenario xx could save $YY millions in healthcare costs and prevent xxx CVD events over 20 years in your city”'
            },
            {
                title: 'Health System Cost ($)',
                chartData: this.buildChart(['Base', 'Current', 'Difference'], [totalHealthBase / healthDiv, totalHealthCurrent / healthDiv, Math.abs(totalHealthCurrent - totalHealthBase) / healthDiv], healthUnit),
                chartOptions: this.barOptions(healthUnit, healthSuggested),
                rows: [
                    { label: 'Primary Care', base: this.formatWithUnit(primaryBase, healthUnit), scenario: this.formatWithUnit(primaryCurrent, healthUnit), trend: this.trendOf(primaryBase, primaryCurrent) },
                    { label: 'Secondary Care', base: this.formatWithUnit(secondaryBase, healthUnit), scenario: this.formatWithUnit(secondaryCurrent, healthUnit), trend: this.trendOf(secondaryBase, secondaryCurrent) }
                ],
                total: {
                    base: this.formatWithUnit(totalHealthBase, healthUnit),
                    scenario: this.formatWithUnit(totalHealthCurrent, healthUnit),
                    trend: this.trendOf(totalHealthBase, totalHealthCurrent)
                },
                suggestion: '“Implementing scenario xx could save $YY millions in healthcare costs and prevent xxx CVD events over 20 years in your city”'
            },
            {
                title: 'Broader Societal Cost ($)',
                chartData: this.buildChart(['Base', 'Current', 'Difference'], [socialTotalBase / socialDiv, socialTotalCurrent / socialDiv, Math.abs(socialTotalCurrent - socialTotalBase) / socialDiv], socialUnit),
                chartOptions: this.barOptions(socialUnit, socialSuggested),
                rows: [
                    { label: 'Productivity patients and carers', base: this.formatWithUnit(productivityBase, socialUnit), scenario: this.formatWithUnit(productivityCurrent, socialUnit), trend: this.trendOf(productivityBase, productivityCurrent) },
                    { label: 'Funeral', base: this.formatWithUnit(funeralBase, socialUnit), scenario: this.formatWithUnit(funeralCurrent, socialUnit), trend: this.trendOf(funeralBase, funeralCurrent) }
                ],
                total: {
                    base: this.formatWithUnit(socialTotalBase, socialUnit),
                    scenario: this.formatWithUnit(socialTotalCurrent, socialUnit),
                    trend: this.trendOf(socialTotalBase, socialTotalCurrent)
                },
                suggestion: '“Implementing scenario xx could save $YY millions in healthcare costs and prevent xxx CVD events over 20 years in your city”'
            }
        ];

        this.data.cards = nextCards;
        setTimeout(() => {
            this.applyHatch();
            this.data.cards = [...this.data.cards];
        }, 0);
    }

    // --- Key builders (include snake_case and camelCase variants) ---
    private keyRangeVariants(range: RangeKey): string[] {
        return range === 'fifteen' ? ['fifteen', 'fifeen'] : [range];
    }

    private keysInterventionCapital(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs)
            v.push(
                `total_investmentcost_10_interventions_current_${r}`,
                `total_investment_cost_10_interventions_current_${r}`,
                `total_investmentcost_10_interventions_${r}`,
                `total_investment_cost_10_interventions_${r}`,
                `totalInvestmentCost10InterventionsCurrent${this.cap(r)}`,
                `totalInvestmentCost10Interventions${this.cap(r)}`
            );
        return v;
    }

    private costAnalysisBarOptionsSimple(suggestedMax: number) {
        return {
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                    labels: {
                        color: 'var(--text-color, #334155)',
                        boxWidth: 12,
                        boxHeight: 12,
                        padding: 10,
                        font: { size: 11 }
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: true,
                    callbacks: {
                        title: (items: any[]) => items?.[0]?.label ?? '',
                        label: (ctx: any) => {
                            const v = Number(ctx.parsed?.y ?? 0); // already scaled to M/B
                            const unitSuffix = /bill/i.test(String(ctx.dataset?.label || '')) ? 'B' : 'M';
                            if (v === 0) return `${ctx.dataset?.label || ''}: 0${unitSuffix}`;
                            if (Number.isInteger(v)) return `${ctx.dataset?.label || ''}: ${Math.trunc(v)}${unitSuffix}`;
                            return `${ctx.dataset?.label || ''}: ${v.toFixed(2)}${unitSuffix}`;
                        }
                    }
                }
            },
            scales: {
                x: { grid: { color: 'rgba(100,116,139,0.12)' }, ticks: { color: 'var(--text-secondary-color, #64748B)', font: { size: 10 } } },
                y: {
                    beginAtZero: true,
                    suggestedMax,
                    ticks: { color: 'var(--text-secondary-color, #64748B)', font: { size: 10 } },
                    grid: { color: 'rgba(100,116,139,0.12)' }
                }
            },
            barPercentage: 0.5,
            animation: {
                duration: 500,
                easing: 'easeOutQuart',
                onComplete(ctx: any) {
                    const chart = ctx.chart;
                    const { ctx: c } = chart;
                    c.save();
                    c.textAlign = 'center';
                    c.textBaseline = 'bottom';
                    c.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--text-color') || '#334155';
                    c.font = '600 10px sans-serif';

                    chart.data.datasets.forEach((ds: any, di: number) => {
                        const meta = chart.getDatasetMeta(di);
                        const unitSuffix = /bill/i.test(String(ds.label || '')) ? 'B' : 'M';
                        meta.data.forEach((bar: any, i: number) => {
                            const rawScaled = Number(ds.data?.[i] ?? 0);
                            const unitSuffix = /bill/i.test(String(ds.label || '')) ? 'B' : 'M';
                            const label = rawScaled === 0 ? `0${unitSuffix}` : Number.isInteger(rawScaled) ? `${Math.trunc(rawScaled)}${unitSuffix}` : `${rawScaled.toFixed(2)}${unitSuffix}`;
                            c.fillText(label, bar.x, bar.y - 4);
                        });
                    });
                    c.restore();
                }
            }
        };
    }

    private keysInterventionRunning(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs)
            v.push(
                `total_running_cost_10_interventions_current_${r}`,
                `total_running_cost_10 _interventions_current_${r}`,
                `total_running_cost_10_interventions_${r}`,
                `total_running_cost_10 _interventions_${r}`,
                `totalRunningCost10InterventionsCurrent${this.cap(r)}`,
                `totalRunningCost10Interventions${this.cap(r)}`
            );
        return v;
    }

    private keysInterventionTotal(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs)
            v.push(
                `cummulative_intervention_cost_current_${r}`,
                `cumulative_intervention_cost_current_${r}`,
                `cummulative_intervention_cost_${r}`,
                `cumulative_intervention_cost_${r}`,
                `cumulativeInterventionCostCurrent${this.cap(r)}`,
                `cumulativeInterventionCost${this.cap(r)}`
            );
        return v;
    }

    private keysPrimaryCare(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs) v.push(`cumulative_primary_care_cost_current_${r}`, `cumulative_primary_care_cost_${r}`, `cumulativePrimaryCareCostCurrent${this.cap(r)}`, `cumulativePrimaryCareCost${this.cap(r)}`);
        return v;
    }

    private keysSecondaryCare(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs)
            v.push(
                `cumulative_CVD_event_cost_current_${r}`,
                `cumulative_cvd_event_cost_current_${r}`,
                `cumulative_CVD_event_cost_${r}`,
                `cumulative_cvd_event_cost_${r}`,
                `cumulativeCvdEventCostCurrent${this.cap(r)}`,
                `cumulativeCvdEventCost${this.cap(r)}`
            );
        return v;
    }

    private keysHealthSectorTotal(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs)
            v.push(
                `cummulative_health_sector_cost_current_${r}`,
                `cumulative_health_sector_cost_current_${r}`,
                `cummulative_health_sector_cost_${r}`,
                `cumulative_health_sector_cost_${r}`,
                `cumulativeHealthSectorCostCurrent${this.cap(r)}`,
                `cumulativeHealthSectorCost${this.cap(r)}`
            );
        return v;
    }

    private keysProductivity(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs) v.push(`total_productivity_loss_current_${r}`, `total_productivity_loss_${r}`, `totalProductivityLossCurrent${this.cap(r)}`, `totalProductivityLoss${this.cap(r)}`);
        return v;
    }

    private keysFuneral(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs) v.push(`total_funeral_cost_current_${r}`, `total_funeral_cost_${r}`, `totalFuneralCostCurrent${this.cap(r)}`, `totalFuneralCost${this.cap(r)}`);
        return v;
    }

    private keysSocialTotal(range: RangeKey): string[] {
        const rs = this.keyRangeVariants(range);
        const v: string[] = [];
        for (const r of rs) v.push(`cumulative_social_cost_current_${r}`, `cumulative_social_cost_${r}`, `cumulativeSocialCostCurrent${this.cap(r)}`, `cumulativeSocialCost${this.cap(r)}`);
        return v;
    }

    // Fix: accept string because keyRangeVariants returns string[]
    private cap(r: string) {
        return r.charAt(0).toUpperCase() + r.slice(1);
    }

    private valueAt(cm: Record<string, Record<string, number>>, keys: string[], targetYear: string, fallbackSum?: number, preferCurrent?: boolean): number {
        const series = this.resolveSeries(cm, keys, preferCurrent);
        if (!series) return typeof fallbackSum === 'number' ? fallbackSum : 0;

        if (Object.prototype.hasOwnProperty.call(series, targetYear)) {
            return Number(series[targetYear] ?? 0);
        }
        const years = Object.keys(series).sort((a, b) => Number(a) - Number(b));
        const use = targetYear === '2011' ? years[0] : years[years.length - 1];
        return Number(series[use] ?? 0);
    }

    private resolveSeries(cm: Record<string, Record<string, number>>, keys: string[], preferCurrent?: boolean): Record<string, number> | null {
        if (!cm) return null;
        for (const k of keys) {
            if (cm[k]) return cm[k]!;
        }
        const allKeys = Object.keys(cm);
        const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        const variants = keys.map((k) => norm(k.replace(/current/gi, '')));
        const candidates = allKeys.map((k) => ({ key: k, n: norm(k) })).filter((c) => variants.some((v) => c.n.includes(v)));

        if (preferCurrent) {
            const withCurrent = candidates.find((c) => c.n.includes('current'));
            if (withCurrent) return cm[withCurrent.key]!;
        }
        const any = candidates[0]?.key;
        return any ? cm[any]! : null;
    }

    private probe(label: string, keys: string[], base: Record<string, Record<string, number>>, current: Record<string, Record<string, number>>) {
        if (!this.debug) return;
        const find = (cm: any) => {
            for (const k of keys) if (cm?.[k]) return k;
            return null;
        };
        const foundBaseKey = find(base);
        const foundCurrentKey = find(current);
        const baseYears = foundBaseKey ? Object.keys(base[foundBaseKey]!) : [];
        const currentYears = foundCurrentKey ? Object.keys(current[foundCurrentKey]!) : [];
        console.log('CostAnalysis probe:', { label, triedKeys: keys, foundBaseKey, foundCurrentKey, baseYears, currentYears });
    }

    private hatchThirdBar(ds: any, hatchColor = '#6AA4E6', alpha = 0.28): any {
        const bg = Array.isArray(ds.backgroundColor) ? [...ds.backgroundColor] : [ds.backgroundColor ?? '#DFEEFF', ds.backgroundColor ?? '#DFEEFF', ds.backgroundColor ?? '#DFEEFF'];
        // bg[2] = this.createHatchPattern(hatchColor, alpha);
        return { ...ds, backgroundColor: bg };
    }

    private createHatchPattern(color = '#6AA4E6', alpha = 0.28): CanvasPattern | string {
        if (typeof document === 'undefined') return this.withAlpha(color, alpha);
        const size = 8;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return this.withAlpha(color, alpha);
        ctx.strokeStyle = this.withAlpha(color, alpha);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, size);
        ctx.lineTo(size, 0);
        ctx.stroke();
        return ctx.createPattern(canvas, 'repeat') as CanvasPattern;
    }

    private withAlpha(hexOrRgb: string, alpha: number): string {
        if (hexOrRgb.startsWith('#') && (hexOrRgb.length === 7 || hexOrRgb.length === 4)) {
            const hex = hexOrRgb.length === 7 ? hexOrRgb : '#' + [...hexOrRgb.slice(1)].map((ch) => ch + ch).join('');
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        return hexOrRgb;
    }

    // Adaptive chart builder with unit
    private buildChart(labels: string[], valuesScaled: number[], unit: 'M' | 'B') {
        return {
            labels,
            datasets: [
                {
                    label: unit === 'B' ? 'Billions' : 'Millions',
                    data: valuesScaled,
                    backgroundColor: ['#d41c58', '#eb6e97', '#fec2d6'],
                    borderColor: ['#d41c58', '#eb6e97', '#fec2d6'],
                    borderWidth: 1,
                    borderRadius: 3
                }
            ]
        };
    }

    // Update this in src/app/pages/cost-metrics/simulation-panel/cost-analysis/cost-analysis.ts

    private barOptions(unit: 'M' | 'B', suggestedMax: number) {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: false },
            scales: {
                // Remove vertical grid lines
                x: {
                    stacked: true,
                    grid: { display: false }
                },
                // Remove horizontal grid lines
                y: {
                    stacked: true,
                    beginAtZero: true,
                    grid: {
                        // display: false
                        // To enable only horizontal grid lines, uncomment:
                        display: true,
                        color: 'rgba(100,116,139,0.12)'
                    },
                    ticks: {
                        callback: (v: any) => `${Math.round(Number(v))}${unit}`,
                        precision: 0
                    },
                    suggestedMax
                }
            },
            animation: {
                duration: 500,
                easing: 'easeOutQuart',
                onComplete: (ctx: any) => this.drawValueLabels(ctx.chart, unit)
            },
            onResize: (chart: any) => {
                setTimeout(() => this.drawValueLabels(chart, unit), 0);
            }
        };
    }

    private drawValueLabels(chart: any, unit: 'M' | 'B') {
        const c = chart?.ctx;
        if (!c) return;
        c.save();
        c.textAlign = 'center';
        c.textBaseline = 'bottom';
        c.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--text-color') || '#334155';
        c.font = '600 10px sans-serif';

        chart.data.datasets.forEach((ds: any, di: number) => {
            const meta = chart.getDatasetMeta(di);
            meta?.data?.forEach((bar: any, i: number) => {
                const v = Number(ds.data?.[i] ?? 0); // already scaled to M/B in chartData
                const label = `${v.toFixed(2)}${unit}`;
                c.fillText(label, bar.x, bar.y - 4);
            });
        });

        c.restore();
    }

    private suggestedMax(values: number[]): number {
        const max = Math.max(...values, 1);
        // Round up for nicer axis; ensure at least small headroom
        const headroom = max * 1.2;
        // If values are tiny, keep a minimum to avoid cramped bars
        const minSuggested = 10;
        return Math.max(Math.ceil(headroom), minSuggested);
    }

    private trendOf(base: number, current: number): 'up' | 'down' | 'flat' {
        if (current > base) return 'up';
        if (current < base) return 'down';
        return 'flat';
    }

    // Adaptive formatter for rows/totals
    // private formatWithUnit(n: number): string {
    //   const abs = Math.abs(n);
    //   if (abs >= 1_000_000_000) {
    //     const billions = n / 1_000_000_000;
    //     return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(billions)}B`;
    //   }
    //   const millions = n / 1_000_000;
    //   return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(millions)}M`;
    // }

    // Unit chooser
    private chooseUnit(maxValue: number): 'M' | 'B' {
        return Math.abs(maxValue) >= 1_000_000_000 ? 'B' : 'M';
    }

    private formatWithUnit(n: number, unit?: 'M' | 'B'): string {
        const u = unit ?? this.chooseUnit(n);
        const div = u === 'B' ? 1_000_000_000 : 1_000_000;
        const scaled = n / div;

        if (scaled === 0) return `0${u}`;
        if (Number.isInteger(scaled)) {
            return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(scaled)}${u}`;
        }
        return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(scaled)}${u}`;
    }
}
